import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export type IncidentSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type IncidentStatus = 'created' | 'acknowledged' | 'resolved' | 'archived';
export type IncidentSource = 'manual' | 'automated';

/**
 * One device's participation in a cross-device incident. Sent by the
 * detail endpoint (`GET /api/incidents/{id}`) only — list endpoints
 * omit the field to keep responses flat.
 *
 * `role` is open-ended on the backend; the UI special-cases
 * "majority"/"minority" (for VLAN-naming-style consistency findings)
 * and renders anything else as a neutral chip.
 */
export interface IncidentAffectedDevice {
  deviceId: string;
  deviceName: string;
  role: string;
  note: string | null;
}

/**
 * A scripted remediation the raising audit offers for this incident. The `id`
 * is what we send back as `remediationId` when creating a change. Populated by
 * the detail endpoint only.
 */
export interface IncidentRemediationOption {
  id: string;
  title: string;
  description: string;
  stepCount: number;
}

export interface Incident {
  id: string;
  title: string;
  description: string | null;
  status: IncidentStatus;
  severity: IncidentSeverity;
  source: IncidentSource;
  organizationId: string;
  divisionId: string | null;
  regionId: string | null;
  deviceId: string | null;
  taskId: string | null;
  dedupeKey: string | null;
  /**
   * The finding this incident is one audience's view of. One evaluation produces one
   * incident per division that owns some of its affected devices, and this is what
   * groups them: non-null means siblings may exist, and
   * `GET /api/incidents/{id}/related` returns the ones you may read.
   */
  correlationKey: string | null;
  createdBy: string | null;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Why this incident is listed alongside the one that was asked about — `partition`
   * (two audiences' views of one occurrence of one finding) today. Populated by
   * `GET /api/incidents/{id}/related` only, where every row is relative to the
   * incident in the path; undefined everywhere else.
   */
  relation?: string;
  /** Populated by the detail endpoint only; undefined elsewhere. */
  affectedDevices?: IncidentAffectedDevice[];
  /**
   * Scripted remediation options the audit proposed. Detail endpoint only;
   * undefined when the incident has no scripted fix.
   */
  remediations?: IncidentRemediationOption[];
  /**
   * The open incident this one is filed under (incident rollup). Non-null means "still
   * open, but a broader incident explains it" — never "resolved". Kept after this
   * incident resolves, so its history can say what it was part of.
   */
  rolledUpIntoId: string | null;
  /** What this incident is in the rollup rule table's terms (`device_unreachable`, …). */
  rollupKind: string | null;
  /** Open incidents rolled up into this one. List endpoints only, and only when non-zero. */
  rolledUpCount?: number;
  /**
   * Recorded and announced to nobody: the producer marked the finding as hygiene. Its
   * severity still says how bad it is.
   */
  quiet: boolean;
}

export interface IncidentEvent {
  id: string;
  incidentId: string;
  eventType: string;
  content: string | null;
  metadata: Record<string, unknown> | null;
  // `null` indicates a system-triggered event (automated audit
  // upsert, auto-resolve, gateway-driven detector). The history view
  // renders these as "System" + the source task from metadata.
  createdBy: string | null;
  createdAt: string;
}

interface IncidentsListResponse {
  incidents: Incident[];
  total: number;
  /** Per-severity availability, ignoring the severity filter itself. */
  severityCounts?: Partial<Record<IncidentSeverity, number>>;
}

interface IncidentEventsResponse {
  events: IncidentEvent[];
}

export interface CreateIncidentRequest {
  title: string;
  description?: string;
  severity: IncidentSeverity;
  divisionId?: string;
  regionId?: string;
  deviceId?: string;
}

export interface UpdateIncidentRequest {
  title?: string;
  description?: string;
  severity?: IncidentSeverity;
  deviceId?: string;
}

export interface AddCommentRequest {
  content: string;
}

export interface ListIncidentsFilters {
  status?: string;
  /**
   * Severities to include. Sent as a comma-separated set; an empty or absent
   * array means "no severity filter" (the server returns all of them).
   */
  severities?: IncidentSeverity[];
  source?: IncidentSource;
  taskId?: string;
  regionId?: string;
  divisionId?: string;
  deviceId?: string;
  includeArchived?: boolean;
  /** Exclude resolved incidents (UI "hide resolved" toggle). */
  excludeResolved?: boolean;
  /** Include open incidents rolled up under a broader one (hidden by default). */
  includeRolledUp?: boolean;
  /** Only the incidents rolled up into this one, open and resolved — a parent's members. */
  rolledUpInto?: string;
  /** `true`: only quiet incidents. `false`: only those that notify. Absent: both. */
  quiet?: boolean;
  /** 1-based page. When set, the response is a single page + a total count. */
  page?: number;
  perPage?: number;
}

export interface PaginatedIncidents {
  incidents: Incident[];
  total: number;
  /**
   * How many incidents exist at each severity, honoring every filter *except*
   * severity — so the UI can say what the current selection is holding back.
   */
  severityCounts: Partial<Record<IncidentSeverity, number>>;
}

const backendApi = config.backendApi;

function incidentParams(filters?: ListIncidentsFilters): string {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.severities?.length) params.append('severity', filters.severities.join(','));
  if (filters?.source) params.append('source', filters.source);
  if (filters?.taskId) params.append('task_id', filters.taskId);
  if (filters?.regionId) params.append('region_id', filters.regionId);
  if (filters?.divisionId) params.append('division_id', filters.divisionId);
  if (filters?.deviceId) params.append('device_id', filters.deviceId);
  if (filters?.includeArchived) params.append('include_archived', 'true');
  if (filters?.excludeResolved) params.append('exclude_resolved', 'true');
  if (filters?.includeRolledUp) params.append('include_rolled_up', 'true');
  if (filters?.rolledUpInto) params.append('rolled_up_into', filters.rolledUpInto);
  if (filters?.quiet !== undefined) params.append('quiet', String(filters.quiet));
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.perPage) params.append('per_page', String(filters.perPage));
  return params.toString();
}

const fetchIncidents = async (filters?: ListIncidentsFilters): Promise<Incident[]> => {
  const qs = incidentParams(filters);
  const url = `${backendApi}/api/incidents${qs ? `?${qs}` : ''}`;
  const response = await axios.get<IncidentsListResponse>(url, { withCredentials: true });
  return response.data.incidents;
};

const fetchIncidentsPage = async (filters?: ListIncidentsFilters): Promise<PaginatedIncidents> => {
  const qs = incidentParams(filters);
  const url = `${backendApi}/api/incidents${qs ? `?${qs}` : ''}`;
  const response = await axios.get<IncidentsListResponse>(url, { withCredentials: true });
  return {
    incidents: response.data.incidents,
    total: response.data.total,
    severityCounts: response.data.severityCounts ?? {},
  };
};

const fetchDeviceIncidents = async (deviceId: string): Promise<Incident[]> => {
  const response = await axios.get<IncidentsListResponse>(
    `${backendApi}/api/devices/${deviceId}/incidents`,
    { withCredentials: true }
  );
  return response.data.incidents;
};

const fetchRegionIncidents = async (regionId: string): Promise<Incident[]> => {
  const response = await axios.get<IncidentsListResponse>(
    `${backendApi}/api/regions/${regionId}/incidents`,
    { withCredentials: true }
  );
  return response.data.incidents;
};

const fetchIncident = async (incidentId: string): Promise<Incident> => {
  const response = await axios.get<Incident>(`${backendApi}/api/incidents/${incidentId}`, {
    withCredentials: true
  });
  return response.data;
};

/**
 * The other partitions of this incident's finding — the ones this user may see.
 *
 * One evaluation produces one incident per division that owns some of its devices, so
 * an organization-level reader gets the whole picture here and a division reader gets
 * only their own. The filtering is the server's; there is nothing to re-derive.
 */
const fetchRelatedIncidents = async (incidentId: string): Promise<Incident[]> => {
  const response = await axios.get<IncidentsListResponse>(
    `${backendApi}/api/incidents/${incidentId}/related`,
    { withCredentials: true }
  );
  return response.data.incidents;
};

const fetchIncidentEvents = async (incidentId: string): Promise<IncidentEvent[]> => {
  const response = await axios.get<IncidentEventsResponse>(
    `${backendApi}/api/incidents/${incidentId}/events`,
    { withCredentials: true }
  );
  return response.data.events;
};

/** One tag-distinct series on an incident's metric chart. */
export interface IncidentMetricSeries {
  /** Legend name built from the identifying tags; empty for a single-series metric. */
  label: string;
  labels: Record<string, string>;
  /** Whether this series crosses the threshold somewhere in the returned range. */
  breaching: boolean;
  /** Worst reading in range, in the direction the condition tests. */
  peak: number | null;
  /**
   * `[epochMillis, value]`, ascending. A `null` value is a bucket the store
   * knows nothing about — a poll that never arrived, or an interval whose only
   * reading was a counter reset. Never render it as zero: a line that dips to
   * the floor and a line that stops are different claims.
   */
  points: [number, number | null][];
}

/**
 * The metric behind a metric-driven incident, over the incident's lifetime.
 *
 * This is where the moving number lives. It used to be interpolated into the
 * incident description, which rewrote the incident on every evaluation and
 * filled its timeline with `field_changed` events; the description now states
 * the rule and holds still.
 *
 * Only metric alerts have one — everything else 404s, which the hook reports as
 * `null` so the caller renders no chart.
 */
export interface IncidentMetricSeriesResponse {
  metric: string;
  conditionType: 'threshold' | 'threshold_ratio' | 'delta' | 'absence';
  operator: string | null;
  /** After any per-device override, so it is the line that actually fired. */
  threshold: number | null;
  unit: string | null;
  windowMinutes: number;
  start: number;
  end: number;
  /** When the incident opened — `start` is this less the lead-in. */
  incidentStarted: number;
  /** Incident still open: `end` is "now" and the chart keeps refetching. */
  live: boolean;
  /** Bucket width when the range was downsampled server-side. */
  bucketSeconds: number | null;
  bucketFunction: string | null;
  series: IncidentMetricSeries[];
  omittedSeries: number;
  /** Nothing in range breaches; `series` shows the closest instead. */
  noBreach: boolean;
  /**
   * A ratio condition whose reference metric has no data, so no percentage can
   * be computed and `series` is empty. Distinct from "the device is quiet" —
   * and it is also why the alert cannot fire on this device.
   */
  referenceMissing: boolean;
}

const fetchIncidentMetricSeries = async (
  incidentId: string
): Promise<IncidentMetricSeriesResponse | null> => {
  try {
    const response = await axios.get<IncidentMetricSeriesResponse>(
      `${backendApi}/api/incidents/${incidentId}/metric-series`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    // A 404 is the documented answer for "this incident has no metric" — a
    // device audit, a manual incident, an alert whose config no longer parses.
    // That is not an error to surface; it means draw nothing.
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

const createIncident = async (data: CreateIncidentRequest): Promise<Incident> => {
  const response = await axios.post<Incident>(`${backendApi}/api/incidents`, data, {
    withCredentials: true
  });
  return response.data;
};

const updateIncident = async (incidentId: string, data: UpdateIncidentRequest): Promise<Incident> => {
  const response = await axios.put<Incident>(
    `${backendApi}/api/incidents/${incidentId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const acknowledgeIncident = async (incidentId: string): Promise<Incident> => {
  const response = await axios.post<Incident>(
    `${backendApi}/api/incidents/${incidentId}/acknowledge`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const resolveIncident = async (incidentId: string): Promise<Incident> => {
  const response = await axios.post<Incident>(
    `${backendApi}/api/incidents/${incidentId}/resolve`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const archiveIncident = async (incidentId: string): Promise<Incident> => {
  const response = await axios.post<Incident>(
    `${backendApi}/api/incidents/${incidentId}/archive`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const addComment = async (incidentId: string, data: AddCommentRequest): Promise<IncidentEvent> => {
  const response = await axios.post<IncidentEvent>(
    `${backendApi}/api/incidents/${incidentId}/comments`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

export function useIncidents(
  organizationId: string | null | undefined,
  filters?: ListIncidentsFilters
) {
  const contextReady = useActiveContextReady();

  return useQuery<Incident[]>({
    queryKey: ['incidents', organizationId, filters],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required to fetch incidents.');
      }
      return fetchIncidents(filters);
    },
    enabled: !!organizationId && contextReady,
  });
}

/**
 * Paginated incident list: returns one page plus the full match count.
 * Pass `filters.page` / `filters.perPage`. `placeholderData` keeps the
 * previous page visible while the next loads (no flash of empty table).
 */
export function useIncidentsPage(
  organizationId: string | null | undefined,
  filters: ListIncidentsFilters
) {
  const contextReady = useActiveContextReady();

  return useQuery<PaginatedIncidents>({
    // Under the ['incidents', …] prefix, not a key of its own: every mutation and
    // both websocket handlers invalidate ['incidents'], and React Query matches
    // element-by-element, so a top-level 'incidents-page' was reached by none of
    // them — acknowledge/resolve left this list stale until a reload. Slot two is
    // already the discriminator here ('device', 'region'); 'page' joins them.
    queryKey: ['incidents', 'page', organizationId, filters],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required to fetch incidents.');
      }
      return fetchIncidentsPage(filters);
    },
    enabled: !!organizationId && contextReady,
    placeholderData: (prev) => prev,
  });
}

export function useDeviceIncidents(deviceId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Incident[]>({
    queryKey: ['incidents', 'device', deviceId],
    queryFn: () => {
      if (!deviceId) throw new Error('deviceId required');
      return fetchDeviceIncidents(deviceId);
    },
    enabled: !!deviceId && contextReady,
  });
}

export function useRegionIncidents(regionId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Incident[]>({
    queryKey: ['incidents', 'region', regionId],
    queryFn: () => {
      if (!regionId) throw new Error('regionId required');
      return fetchRegionIncidents(regionId);
    },
    enabled: !!regionId && contextReady,
  });
}

export function useIncident(incidentId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Incident>({
    queryKey: ['incident', incidentId],
    queryFn: () => {
      if (!incidentId) {
        throw new Error('incidentId is required to fetch incident.');
      }
      return fetchIncident(incidentId);
    },
    enabled: !!incidentId && contextReady,
  });
}

/**
 * Every incident related to this one, whatever the reason, each carrying its own
 * `relation`.
 *
 * Deliberately not gated on `correlationKey`. It used to be, as a way to skip the
 * request for the many incidents that have no siblings — but relatedness is a stored
 * edge now, not something derived from the correlation, so a link between two
 * incidents that share no finding (an operator-asserted root cause, say) has no
 * correlation to gate on and would never be fetched. The unconditional request is a
 * primary-key lookup that returns an empty list.
 */
export function useRelatedIncidents(incidentId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Incident[]>({
    queryKey: ['incident-related', incidentId],
    queryFn: () => {
      if (!incidentId) {
        throw new Error('incidentId is required to fetch related incidents.');
      }
      return fetchRelatedIncidents(incidentId);
    },
    enabled: !!incidentId && contextReady,
  });
}

export function useIncidentEvents(incidentId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<IncidentEvent[]>({
    queryKey: ['incident-events', incidentId],
    queryFn: () => {
      if (!incidentId) {
        throw new Error('incidentId is required to fetch incident events.');
      }
      return fetchIncidentEvents(incidentId);
    },
    enabled: !!incidentId && contextReady,
  });
}

/**
 * `incidentLive` keeps an open incident's chart current without polling a
 * resolved one forever. One minute matches the collectors' sampling rate;
 * evaluation runs every five, so anything faster would redraw the same points.
 */
export function useIncidentMetricSeries(incidentId: string | undefined, incidentLive: boolean) {
  const contextReady = useActiveContextReady();

  return useQuery<IncidentMetricSeriesResponse | null>({
    queryKey: ['incident-metric-series', incidentId],
    queryFn: () => {
      if (!incidentId) {
        throw new Error('incidentId is required to fetch an incident metric series.');
      }
      return fetchIncidentMetricSeries(incidentId);
    },
    enabled: !!incidentId && contextReady,
    refetchInterval: incidentLive ? 60_000 : false,
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateIncidentRequest) => createIncident(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: UpdateIncidentRequest }) =>
      updateIncident(incidentId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['incident', variables.incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

export function useAcknowledgeIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (incidentId: string) => acknowledgeIncident(incidentId),
    onSuccess: (_, incidentId) => {
      void queryClient.invalidateQueries({ queryKey: ['incident', incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['incident-events', incidentId] });
    },
  });
}

export function useResolveIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (incidentId: string) => resolveIncident(incidentId),
    onSuccess: (_, incidentId) => {
      void queryClient.invalidateQueries({ queryKey: ['incident', incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['incident-events', incidentId] });
    },
  });
}

export function useArchiveIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (incidentId: string) => archiveIncident(incidentId),
    onSuccess: (_, incidentId) => {
      void queryClient.invalidateQueries({ queryKey: ['incident', incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['incident-events', incidentId] });
    },
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: AddCommentRequest }) =>
      addComment(incidentId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['incident-events', variables.incidentId] });
    },
  });
}

// ─── Suppressions ────────────────────────────────────────────────────
//
// Suppression is stored against the *alert definition* (an `alert_overrides` row:
// a scope plus a glob dedupe pattern), so it is not a field on the incident and it
// is not something this file can derive. One override can silence a whole region.
// The backend runs the evaluator's own matcher and tells us what is silencing this
// incident — deliberately not re-implemented here, because a second copy of those
// rules would be free to disagree with the one that actually suppresses the alert,
// and the symptom would be a page that confidently says the wrong thing.

/** One override currently silencing this incident's alert. */
export interface IncidentSuppression {
  /** The `alert_overrides` row id — what the delete call takes. */
  id: string;
  taskId: string;
  overrideType: string;
  deviceId: string | null;
  regionId: string | null;
  divisionId: string | null;
  /** `null` = indefinite. Expired silences are never returned. */
  silenceUntil: string | null;
  silenceReason: string | null;
  /** `null` = scope-only, so it silences every finding in scope, not just this one. */
  dedupePattern: string | null;
  /** True when the override targets only this finding. */
  scopedToThisIncident: boolean;
  createdBy: string;
  createdAt: string;
}

interface IncidentSuppressionsResponse {
  suppressions: IncidentSuppression[];
}

/** Open incidents *other than this one* that the same override holds down. */
export interface SuppressionImpact {
  otherOpenIncidents: number;
}

export interface CreateIncidentSuppressionRequest {
  /** Hours to silence for; omitted or 0 means indefinitely. */
  silenceHours?: number;
  silenceReason?: string;
  /**
   * How wide to silence.
   *
   * `incident` (the default) silences this partition alone — the server derives the
   * pattern from this incident's own key, so the blast radius is provably the devices
   * you administer. `finding` silences every partition of the same finding, present
   * and future, and needs `alert_definition:update`.
   */
  scope?: 'incident' | 'finding';
}

const fetchIncidentSuppressions = async (incidentId: string): Promise<IncidentSuppression[]> => {
  const response = await axios.get<IncidentSuppressionsResponse>(
    `${backendApi}/api/incidents/${incidentId}/suppressions`,
    { withCredentials: true },
  );
  return response.data.suppressions;
};

/**
 * What is silencing this incident's alert right now. An empty array means nothing
 * is — that is the answer the page needs on load, and the reason a suppression made
 * from anywhere (this page, the alert's Overrides panel, a region-wide bulk silence)
 * still shows up here.
 *
 * 404s for incidents no alert fired: manual incidents have nothing to suppress. The
 * caller treats that as "no suppression" rather than an error worth showing.
 */
export function useIncidentSuppressions(incidentId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<IncidentSuppression[]>({
    queryKey: ['incident-suppressions', incidentId],
    queryFn: async () => {
      if (!incidentId) throw new Error('incidentId is required to fetch suppressions.');
      try {
        return await fetchIncidentSuppressions(incidentId);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return [];
        throw err;
      }
    },
    enabled: !!incidentId && contextReady,
  });
}

export function useSuppressIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      data,
    }: {
      incidentId: string;
      data: CreateIncidentSuppressionRequest;
    }) => {
      const response = await axios.post<IncidentSuppression>(
        `${backendApi}/api/incidents/${incidentId}/suppressions`,
        data,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      if (variables.data.scope === 'finding') {
        // A whole-finding pattern silences the sibling partitions too, so every
        // incident's cached suppression list is now potentially wrong — not just
        // this one's. Dropping the prefix is cheaper than working out which.
        void queryClient.invalidateQueries({ queryKey: ['incident-suppressions'] });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['incident-suppressions', variables.incidentId] });
      }
      // The suppression is recorded on the timeline too, and it changes the alert's
      // override list that AlertDetails renders.
      void queryClient.invalidateQueries({ queryKey: ['incident-events', variables.incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides'] });
    },
  });
}

export function useUnsuppressIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ incidentId, overrideId }: { incidentId: string; overrideId: string }) => {
      await axios.delete(
        `${backendApi}/api/incidents/${incidentId}/suppressions/${overrideId}`,
        { withCredentials: true },
      );
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['incident-suppressions', variables.incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['incident-events', variables.incidentId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides'] });
    },
  });
}

/**
 * Fetch how much else a suppression is holding down — for the confirm before
 * removing it.
 *
 * Not a `useQuery`: the backend scans the alert's other open incidents to answer
 * it, and nobody should pay for that just by opening an incident page. Called from
 * the remove handler, and cached by `query` so a cancel-then-retry doesn't
 * re-scan.
 */
export function useSuppressionImpactFetcher() {
  const queryClient = useQueryClient();

  return (incidentId: string, overrideId: string) =>
    queryClient.query<SuppressionImpact>({
      queryKey: ['incident-suppression-impact', incidentId, overrideId],
      queryFn: async () => {
        const response = await axios.get<SuppressionImpact>(
          `${backendApi}/api/incidents/${incidentId}/suppressions/${overrideId}/impact`,
          { withCredentials: true },
        );
        return response.data;
      },
      staleTime: 30_000,
    });
}
